import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

/* Body-portal popover pinned to `anchorRef` with position:fixed, so a phase
 * table's overflow container can never clip it and it always sits above the
 * next phase / line. Flips above the anchor when there is no room below and
 * follows the anchor on scroll / resize. `onDismiss` adds a click-away layer. */
export function AnchoredPortal({
  anchorRef, open, align = "left", offset = 4, onDismiss, children, className = "", style, ...rest
}) {
  const [pos, setPos] = useState(null);
  const ref = useRef(null);

  const place = useCallback(() => {
    const a = anchorRef.current;
    const el = ref.current;
    if (!a || !el) return;
    const r = a.getBoundingClientRect();
    const h = el.offsetHeight;
    const w = el.offsetWidth;
    const vh = window.innerHeight;
    const vw = window.innerWidth;
    const below = vh - r.bottom - offset;
    const up = h > below && r.top - offset > below;
    let top = up ? r.top - offset - h : r.bottom + offset;
    top = Math.max(8, Math.min(top, vh - h - 8));
    let left = align === "right" ? r.right - w : r.left;
    left = Math.max(8, Math.min(left, vw - w - 8));
    setPos({ top, left });
  }, [anchorRef, align, offset]);

  useLayoutEffect(() => {
    if (!open) { setPos(null); return; }
    place();
  }, [open, place]);

  useEffect(() => {
    if (!open) return;
    window.addEventListener("scroll", place, true);
    window.addEventListener("resize", place);
    // Re-place when the content changes size (e.g. a filtering list) so an
    // upward-flipped popover keeps hugging its anchor.
    const ro = ref.current && typeof ResizeObserver !== "undefined" ? new ResizeObserver(place) : null;
    if (ro) ro.observe(ref.current);
    return () => {
      window.removeEventListener("scroll", place, true);
      window.removeEventListener("resize", place);
      if (ro) ro.disconnect();
    };
  }, [open, place]);

  if (!open) return null;
  return createPortal(
    <>
      {onDismiss && (
        <div className="fixed inset-0" style={{ zIndex: 999 }} onClick={onDismiss} data-testid="anchored-portal-backdrop" />
      )}
      <div
        ref={ref}
        className={className}
        style={{
          position: "fixed", zIndex: 1000,
          // Off-screen until placed (layout effect re-renders before paint).
          // No `visibility: hidden` — a hidden element cannot take focus,
          // and menus focus their first item on open.
          top: pos ? pos.top : -9999, left: pos ? pos.left : -9999,
          ...style,
        }}
        {...rest}
      >
        {children}
      </div>
    </>,
    document.body,
  );
}
